// Team-written notes + star ratings for Team Directory contacts.
//
// The contacts themselves live in the shared Google account (their `notes`
// field is the auto-synced deal history and must never be touched by hand),
// so the team's own notes and ratings live here in Firestore instead — one
// doc per contact, keyed by the contact's Google resourceName.
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  arrayUnion,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { replaceNoteByRef, removeNoteByRef } from "./entryNotes";

const COL = "directoryNotes";

// Google resourceNames look like "people/c123456789" — the slash can't be a
// Firestore doc id, so squash anything unsafe to underscores.
export function directoryDocId(resourceName) {
  return String(resourceName || "").replace(/[^\w-]/g, "_");
}

// One read for the whole page: map of resourceName -> { noteEntries, ratings }.
// Only contacts someone has written on have a doc, so this stays tiny even
// with thousands of contacts in the directory.
export async function loadDirectoryExtras() {
  const snap = await getDocs(collection(firestored, COL));
  const map = {};
  snap.docs.forEach((d) => {
    const data = d.data() || {};
    const key = data.resourceName || d.id;
    map[key] = {
      noteEntries: Array.isArray(data.noteEntries) ? data.noteEntries : [],
      ratings: data.ratings || {},
    };
  });
  return map;
}

// Append one attributed note (build it with buildNote from utils/entryNotes).
// arrayUnion so two people adding notes at the same time can't clobber each
// other; setDoc+merge so the first note on a contact creates the doc.
export async function addDirectoryNote(resourceName, note) {
  return setDoc(
    doc(firestored, COL, directoryDocId(resourceName)),
    { resourceName, noteEntries: arrayUnion(note) },
    { merge: true },
  );
}

// Edits and deletes re-read the doc first and address the note by its
// identity, so a colleague's note added since this page loaded is never
// clobbered by an old client-side copy. Both return the fresh note array
// that was saved (so the page can update its local state from truth), or
// null when the note was already gone — surface that instead of writing.
async function rewriteNote(resourceName, apply) {
  const ref = doc(firestored, COL, directoryDocId(resourceName));
  const snap = await getDoc(ref);
  const current = snap.exists() ? snap.data().noteEntries || [] : [];
  const next = apply(current);
  if (next === null) return null;
  await setDoc(ref, { resourceName, noteEntries: next }, { merge: true });
  return next;
}

export async function editDirectoryNote(resourceName, noteRef, newText) {
  return rewriteNote(resourceName, (cur) =>
    replaceNoteByRef(cur, noteRef, newText),
  );
}

export async function deleteDirectoryNote(resourceName, noteRef) {
  return rewriteNote(resourceName, (cur) => removeNoteByRef(cur, noteRef));
}

// Set (or change) one person's 1..5 star vote. Merge keeps everyone else's.
// Refuses to write without a real user id — a phantom "undefined" key would
// count in the team average forever.
export async function setDirectoryRating(resourceName, userId, stars) {
  if (!userId) throw new Error("setDirectoryRating: no user id");
  return setDoc(
    doc(firestored, COL, directoryDocId(resourceName)),
    { resourceName, ratings: { [userId]: stars } },
    { merge: true },
  );
}
